import React, { useState } from 'react';
import { toast } from 'sonner';
import { Upload, FileText, Trash2, CheckCircle, AlertCircle, Lock, ShieldCheck } from 'lucide-react';
import verifiedIcon from '@/assets/verified.png';
import { logger } from '@/utils/logger';
import { captureError } from '@/debug/debugStore';
import { getErrorMessage } from '@/api/errorHandler';
import { useAuthContext } from '../contexts/useAuthContext';
import { uploadTutorFile, getProfile } from '@/api/services/profilesApi';
import { hasTeacherRelatedExperience } from '@/constants/dropdownOptions';
import TooltipIcon from '@/components/common/TooltipIcon';
import type { TutorUploadType } from '@/types/api';
import { useI18n } from '@/i18n';
import { useNavigate } from '@/i18n/navigation';

type UploadSlot = 'id' | 'certificate' | 'support';

interface UploadedFile {
  id: string;
  file: File;
  preview?: string;
  type: UploadSlot;
  name?: string;
}

interface ValidationError {
  id?: string;
  certificate?: string;
  support?: string;
}

const UploadDocumentationPage: React.FC = () => {
  const { locale } = useI18n();
  const navigate = useNavigate();
  const { firebaseUser, loading, profile, updateProfile } = useAuthContext();
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [errors, setErrors] = useState<ValidationError>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const isZh = locale === 'zh-CN';

  // Redirect if not authenticated or not a tutor
  React.useEffect(() => {
    if (!loading) {
      if (!firebaseUser || profile?.role !== 'tutor') {
        navigate('/');
        if (!firebaseUser) {
          toast.error(isZh ? '请先登录以访问此页面' : 'Please log in to access this page');
        } else {
          toast.error(isZh ? '只有导师可以访问此页面' : 'Only tutors can access this page');
        }
      }
    }
  }, [firebaseUser, profile, loading, navigate, isZh]);

  const ALLOWED_FORMATS = ['image/jpeg', 'image/png', 'application/pdf'];
  const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.pdf'];
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  const validateFile = (file: File): string | null => {
    // Check file extension
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
      return isZh ? '不支持此文件格式，请上传 JPG、PNG 或 PDF。' : 'File format not supported. Please upload JPG, PNG, or PDF.';
    }

    // Check MIME type
    if (!ALLOWED_FORMATS.includes(file.type)) {
      return isZh ? '文件类型无效，请上传 JPG、PNG 或 PDF。' : 'Invalid file type. Please upload JPG, PNG, or PDF.';
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return isZh ? '文件大小超过 5MB 限制。' : 'File size exceeds 5MB limit.';
    }

    return null;
  };

  const handleFileSelect = (
    file: File,
    type: UploadSlot,
    name?: string
  ) => {
    const validationError = validateFile(file);
    if (validationError) {
      setErrors({ ...errors, [type]: validationError });
      return;
    }

    setErrors({ ...errors, [type]: undefined });

    // Create preview for images
    let preview: string | undefined;
    if (file.type.startsWith('image/')) {
      preview = URL.createObjectURL(file);
    }

    const uploadedFile = { id: `${type}-${Date.now()}-${file.name}`, file, preview, type, name };
    setUploadedFiles((prev) =>
      type === 'support'
        ? [...prev, uploadedFile]
        : [...prev.filter((f) => f.type !== type), uploadedFile]
    );
    window.__debugBridge?.addBreadcrumb({
      category: 'upload',
      message: 'Selected upload document',
      metadata: {
        slot: type,
        mimeType: file.type || 'unknown',
        sizeBytes: file.size,
        extension: file.name.split('.').pop()?.toLowerCase() ?? null,
      },
    });
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.add('border-primary', 'bg-primary-50');
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('border-primary', 'bg-primary-50');
  };

  const handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    type: UploadSlot
  ) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('border-primary', 'bg-primary-50');

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0], type);
    }
  };

  const removeFile = (id: string) => {
    const file = uploadedFiles.find((f) => f.id === id);
    if (file?.preview) {
      URL.revokeObjectURL(file.preview);
    }
    setUploadedFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const updateSupportDocumentName = (id: string, name: string) => {
    setUploadedFiles((prev) => prev.map((f) => f.id === id ? { ...f, name } : f));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: ValidationError = {};
    const unnamedSupportDocument = uploadedFiles.some((f) => f.type === 'support' && !f.name?.trim());
    if (unnamedSupportDocument) {
      newErrors.support = isZh ? '请先为每份辅助文件填写名称后再上传。' : 'Please name each support document before uploading.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      window.__debugBridge?.addBreadcrumb({
        category: 'upload',
        message: 'Upload submit blocked by validation',
        metadata: { validationErrors: newErrors },
      });
      return;
    }

    if (uploadedFiles.length === 0) {
      window.__debugBridge?.addBreadcrumb({
        category: 'upload',
        message: 'Upload skipped because no documents were selected',
      });
      navigate('/tutor/edit-profile');
      return;
    }

    setIsSubmitting(true);

    try {
      const uploadQueue: { file: File; uploadType: TutorUploadType; name?: string }[] = uploadedFiles.map((f) => ({
        file: f.file,
        uploadType:
          f.type === 'id'
            ? 'identity'
            : f.type === 'certificate'
              ? 'certificate'
              : 'support',
        name: f.name,
      }));

      window.__debugBridge?.addBreadcrumb({
        category: 'upload',
        message: 'Started upload documentation submit',
        metadata: {
          fileCount: uploadQueue.length,
          uploadTypes: uploadQueue.map((item) => item.uploadType),
          files: uploadQueue.map(({ file, uploadType }) => ({
            uploadType,
            mimeType: file.type || 'unknown',
            sizeBytes: file.size,
            extension: file.name.split('.').pop()?.toLowerCase() ?? null,
          })),
        },
      });

      for (let i = 0; i < uploadQueue.length; i++) {
        const { file, uploadType, name } = uploadQueue[i];
        setUploadProgress(isZh ? `上传中 ${i + 1}/${uploadQueue.length}…` : `Uploading ${i + 1}/${uploadQueue.length}…`);
        window.__debugBridge?.addBreadcrumb({
          category: 'upload',
          message: 'Uploading document',
          metadata: {
            index: i + 1,
            total: uploadQueue.length,
            uploadType,
            mimeType: file.type || 'unknown',
            sizeBytes: file.size,
            extension: file.name.split('.').pop()?.toLowerCase() ?? null,
          },
        });
        await uploadTutorFile(uploadType, [file], name ? [name] : undefined);
      }

      setUploadProgress('');
      uploadedFiles.forEach((uploadedFile) => {
        if (uploadedFile.preview) {
          URL.revokeObjectURL(uploadedFile.preview);
        }
      });
      setUploadedFiles([]);
      setErrors({});

      // Refresh profile to reflect updated verificationDocuments and verificationStatus
      const freshProfile = await getProfile('tutor');
      updateProfile(freshProfile);

      toast.success(freshProfile.verificationStatus === 'pending'
        ? (isZh ? '文件上传成功，你的资料正在等待审核。' : 'Documents uploaded successfully. Your profile is pending review.')
        : (isZh ? '文件上传成功，请继续上传其余所需文件。' : 'Documents uploaded successfully. Please upload any remaining required documents.'));
      window.__debugBridge?.addBreadcrumb({
        category: 'upload',
        message: 'Upload documentation submit succeeded',
        metadata: { verificationStatus: freshProfile.verificationStatus ?? null },
      });
    } catch (error) {
      setUploadProgress('');
      toast.error(getErrorMessage(error) || (isZh ? '上传文件失败，请再试一次。' : 'Failed to upload documents. Please try again.'));
      logger.error('UploadDocumentationPage', 'Failed to upload documents', error);
      captureError('Upload:submit', error);
      window.__debugBridge?.addBreadcrumb({
        category: 'upload',
        message: 'Upload documentation submit failed',
        metadata: {
          errorMessage: error instanceof Error ? error.message : String(error),
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check for existing uploaded documents in profile
  const isTeacherRelated = hasTeacherRelatedExperience((profile?.tutorExperienceTypes as string[]) || []);
  const existingIdDoc = profile?.verificationDocuments?.find((d) => d.type === 'identity');
  const existingEducationDoc = profile?.verificationDocuments?.find((d) => d.type === 'qualification');
  const existingCertificateDoc = profile?.verificationDocuments?.find((d) => d.type === 'certificate');
  const existingPublicExamDoc = existingCertificateDoc || existingEducationDoc;
  const existingSupportDocs = profile?.verificationDocuments?.filter((d) => d.type === 'support') || [];

  const idFile = uploadedFiles.find((f) => f.type === 'id');
  const certificateFile = uploadedFiles.find((f) => f.type === 'certificate');
  const supportFiles = uploadedFiles.filter((f) => f.type === 'support');
  const primaryCertificateLabel = isTeacherRelated
    ? isZh
      ? '教学证书 / 教育证书'
      : 'Teaching Certificate / Education Certificate'
    : isZh
      ? '公开考试证书'
      : 'Public Exam Certificate';
  const primaryCertificateTooltip = isTeacherRelated
    ? isZh
      ? '请上传你的教学证书或相关教育证书，以便我们核实你的教学资格。'
      : 'Please upload your teaching certificate or relevant education certificate so we can verify your teaching credentials.'
    : isZh
      ? '请上传你的正式公开考试证书（例如 SPM），以核实你的学术成绩。'
      : 'Please upload your official public examination certificate (e.g., SPM) to verify your academic results.';
  const primaryCertificateReceivedText = isTeacherRelated
    ? isZh
      ? '我们已收到你的教学或教育证书'
      : 'Your teaching or education certificate has been received'
    : isZh
      ? '我们已收到你的公开考试证书'
      : 'Your public exam certificate has been received';

  React.useEffect(() => {
    window.__debugBridge?.registerPageData('UploadDocumentationPage', () => ({
      page: 'UploadDocumentationPage',
      state: {
        isSubmitting,
        uploadProgress,
        selectedSlots: uploadedFiles.map((file) => file.type),
        selectedFileCount: uploadedFiles.length,
        selectedFiles: uploadedFiles.map((uploadedFile) => ({
          slot: uploadedFile.type,
          uploadType:
            uploadedFile.type === 'id'
              ? 'identity'
              : uploadedFile.type === 'certificate'
                ? 'certificate'
                : 'support',
          mimeType: uploadedFile.file.type || 'unknown',
          sizeBytes: uploadedFile.file.size,
          extension: uploadedFile.file.name.split('.').pop()?.toLowerCase() ?? null,
          hasPreview: !!uploadedFile.preview,
          hasSupportDocumentName: uploadedFile.type === 'support' ? !!uploadedFile.name?.trim() : null,
        })),
        validationErrors: errors,
        existingDocuments: {
          identity: !!existingIdDoc,
          qualification: !!existingEducationDoc,
          certificate: !!existingCertificateDoc,
          supportCount: existingSupportDocs.length,
        },
        profileState: {
          role: profile?.role ?? null,
          verificationStatus: profile?.verificationStatus ?? null,
        },
      },
    }));

    return () => {
      window.__debugBridge?.unregisterPageData('UploadDocumentationPage');
    };
  }, [
    certificateFile,
    errors,
    existingCertificateDoc,
    existingEducationDoc,
    existingIdDoc,
    existingSupportDocs.length,
    idFile,
    isSubmitting,
    isTeacherRelated,
    profile?.role,
    profile?.verificationStatus,
    supportFiles,
    uploadProgress,
    uploadedFiles,
  ]);

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">{isZh ? '加载中...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Main Content */}
      <section className="py-8 md:py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            {/* Header */}
            <div className="mb-12">
              <h1 className="text-4xl font-bold text-gray-900 mb-2">{isZh ? '完成你的资料' : 'Complete Your Profile'}</h1>
              <p className="text-lg text-gray-600">{isZh ? '上传审核所需文件' : 'Upload required documents for verification'}</p>
            </div>

            {/* Main Form Card */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Progress Indicator */}
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 flex items-start gap-3 mb-6">
                  <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-primary-900">
                    <p className="font-semibold">{isZh ? '文件审核' : 'Document Verification'}</p>
                    <p className="mt-1 text-primary-800">
                      {isZh
                        ? `上传${isTeacherRelated ? '教学证书 / 教育证书' : '公开考试证书'}或辅助文件以供审核。再补上身份证明并通过审核后，你将获得 Fully Verified 状态，并提升被家长选中的机会。`
                        : `Upload a ${isTeacherRelated ? 'Teaching Certificate / Education Certificate' : 'Public Exam Certificate'} or support document for review. Add your ID to become Fully Verified after approval and increase your chance of being selected by parents.`}
                    </p>
                  </div>
                </div>

                {/* Public Exam Certificate Upload */}
                <div>
                  <div className="mb-3 flex items-center gap-2">
                    <label className="block text-sm font-semibold text-gray-900">
                      {primaryCertificateLabel} <span className="text-red-500">*</span>
                    </label>
                    <TooltipIcon label={`What is a ${primaryCertificateLabel}?`}>
                      {primaryCertificateTooltip}
                    </TooltipIcon>
                  </div>

                  {existingPublicExamDoc ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div className="w-24 h-24 bg-green-100 rounded border border-green-300 flex items-center justify-center">
                            <Lock className="w-8 h-8 text-green-600" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <p className="font-semibold text-gray-900">
                              {isZh ? '已上传' : 'Already uploaded'}
                            </p>
                          </div>
                          <p className="text-sm text-gray-600 mb-1">
                            {primaryCertificateReceivedText}
                          </p>
                          <p className="text-xs text-gray-500">
                            {isZh ? '上传日期 ' : 'Uploaded on '}
                            {new Date(existingPublicExamDoc.uploadedAt || '').toLocaleDateString(locale === 'zh-CN' ? 'zh-CN' : 'en-US')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : certificateFile ? (
                    <div className="bg-accent-50 border border-accent-200 rounded-lg p-4">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          {certificateFile.preview ? (
                            <img
                              src={certificateFile.preview}
                              alt={isTeacherRelated ? (isZh ? '教学或教育证书预览' : 'Teaching or education certificate preview') : (isZh ? '公开考试证书预览' : 'Public exam certificate preview')}
                              className="w-24 h-24 object-cover rounded border border-accent-300"
                            />
                          ) : (
                            <div className="w-24 h-24 bg-accent-100 rounded border border-accent-300 flex items-center justify-center">
                              <FileText className="w-8 h-8 text-accent-600" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle className="w-5 h-5 text-accent" />
                            <p className="font-semibold text-gray-900">
                              {certificateFile.file.name}
                            </p>
                          </div>
                          <p className="text-sm text-gray-600 mb-3">
                            {(certificateFile.file.size / 1024).toFixed(1)} KB
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              removeFile(certificateFile.id);
                            }}
                            className="inline-flex items-center gap-2 text-sm text-red-600 hover:text-red-700 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            {isZh ? '移除' : 'Remove'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, 'certificate')}
                      className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center transition-colors cursor-pointer hover:border-primary hover:bg-primary-50 bg-white"
                    >
                      <input
                        type="file"
                        accept={ALLOWED_EXTENSIONS.join(',')}
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            handleFileSelect(e.target.files[0], 'certificate');
                          }
                        }}
                        className="hidden"
                        id="certificate-upload"
                      />
                      <label htmlFor="certificate-upload" className="cursor-pointer">
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="font-medium text-gray-900">
                          {isZh ? '点击上传或拖放文件' : 'Click to upload or drag and drop'}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          {isZh ? 'PNG、JPG 或 PDF（最大 5 MB）' : 'PNG, JPG, or PDF (max. 5 MB)'}
                        </p>
                      </label>
                    </div>
                  )}
                  {errors.certificate && (
                    <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.certificate}
                    </p>
                  )}
                </div>

                {/* ID Document Upload */}
                <div>
                  <div className="mb-1 flex items-center gap-2">
                    <label className="block text-sm font-semibold text-gray-900">
                      {isZh ? '身份证明文件' : 'Identification Document'}
                    </label>
                    <TooltipIcon label={isZh ? '什么是身份证明文件？' : 'What is an Identification Document?'}>
                      {isZh
                        ? '请上传有效的政府签发身份证、驾照或护照。通过审核后，你可获得 Fully Verified 状态。'
                        : 'Please upload a valid government-issued ID, driving licence, or passport to become Fully Verified after approval.'}
                    </TooltipIcon>
                  </div>
                  <p className="mb-3 text-sm text-gray-600 flex items-center gap-1">
                    {isZh ? '获取 ' : 'Get '}
                    Verified
                    {isZh ? ' 标记，提升曝光度' : ' badge '}
                    <img src={verifiedIcon} alt="Verified" className="h-5 w-5 flex-shrink-0" />
                    {!isZh && 'to boost your visibility'}
                  </p>

                  {existingIdDoc ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div className="w-24 h-24 bg-green-100 rounded border border-green-300 flex items-center justify-center">
                            <Lock className="w-8 h-8 text-green-600" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <p className="font-semibold text-gray-900">
                              {isZh ? '已上传' : 'Already uploaded'}
                            </p>
                          </div>
                          <p className="text-sm text-gray-600 mb-1">
                            {isZh ? '我们已收到你的身份证明文件' : 'Your identification document has been received'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {isZh ? '上传日期 ' : 'Uploaded on '}
                            {new Date(existingIdDoc.uploadedAt || '').toLocaleDateString(locale === 'zh-CN' ? 'zh-CN' : 'en-US')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : idFile ? (
                    <div className="bg-accent-50 border border-accent-200 rounded-lg p-4">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          {idFile.preview ? (
                            <img
                              src={idFile.preview}
                              alt={isZh ? '身份证明预览' : 'ID preview'}
                              className="w-24 h-24 object-cover rounded border border-accent-300"
                            />
                          ) : (
                            <div className="w-24 h-24 bg-accent-100 rounded border border-accent-300 flex items-center justify-center">
                              <FileText className="w-8 h-8 text-accent-600" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle className="w-5 h-5 text-accent" />
                            <p className="font-semibold text-gray-900">
                              {idFile.file.name}
                            </p>
                          </div>
                          <p className="text-sm text-gray-600 mb-3">
                            {(idFile.file.size / 1024).toFixed(1)} KB
                          </p>
                          <button
                            type="button"
                            onClick={() => removeFile(idFile.id)}
                            className="inline-flex items-center gap-2 text-sm text-red-600 hover:text-red-700 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            {isZh ? '移除' : 'Remove'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, 'id')}
                      className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center transition-colors cursor-pointer hover:border-primary hover:bg-primary-50 bg-white"
                    >
                      <input
                        type="file"
                        accept={ALLOWED_EXTENSIONS.join(',')}
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            handleFileSelect(e.target.files[0], 'id');
                          }
                        }}
                        className="hidden"
                        id="id-upload"
                      />
                      <label htmlFor="id-upload" className="cursor-pointer">
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="font-medium text-gray-900">
                          {isZh ? '点击上传或拖放文件' : 'Click to upload or drag and drop'}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          {isZh ? 'PNG、JPG 或 PDF（最大 5 MB）' : 'PNG, JPG, or PDF (max. 5 MB)'}
                        </p>
                      </label>
                    </div>
                  )}
                  {errors.id && (
                    <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.id}
                    </p>
                  )}
                </div>

                {/* Support Document Upload */}
                <div>
                  <div className="mb-3 flex items-center gap-2">
                    <label className="block text-sm font-semibold text-gray-900">
                      {isZh ? '辅助文件' : 'Support Documents'}
                    </label>
                    <TooltipIcon label={isZh ? '什么是辅助文件？' : 'What are Support Documents?'}>
                      {isZh
                        ? '上传任何能加强你资料可信度的补充证明，并在提交前为每份文件填写清晰名称。'
                        : 'Upload any additional supporting proof that strengthens your profile, and give each document a clear name before submitting.'}
                    </TooltipIcon>
                  </div>

                  {existingSupportDocs.length > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                        <div className="flex items-center gap-2 mb-3">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <p className="font-semibold text-gray-900">
                          {isZh ? '已上传的辅助文件' : 'Uploaded support documents'}
                          </p>
                        </div>
                      <ul className="space-y-2 text-sm text-gray-700">
                        {existingSupportDocs.map((doc) => (
                          <li key={doc.url} className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-green-600" />
                            {doc.name || (isZh ? '辅助文件' : 'Support document')}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {supportFiles.length > 0 && (
                    <div className="space-y-3 mb-4">
                      {supportFiles.map((supportFile) => (
                        <div key={supportFile.id} className="bg-accent-50 border border-accent-200 rounded-lg p-4">
                          <div className="flex items-start gap-4">
                            <div className="w-16 h-16 bg-accent-100 rounded border border-accent-300 flex items-center justify-center flex-shrink-0">
                              <FileText className="w-7 h-7 text-accent-600" />
                            </div>
                            <div className="flex-1 space-y-3">
                              <div>
                                <p className="font-semibold text-gray-900">{supportFile.file.name}</p>
                                <p className="text-sm text-gray-600">
                                  {(supportFile.file.size / 1024).toFixed(1)} KB
                                </p>
                              </div>
                              <input
                                type="text"
                                value={supportFile.name || ''}
                                onChange={(e) => updateSupportDocumentName(supportFile.id, e.target.value)}
                                placeholder={isZh ? '文件名称，例如：学校聘书' : 'Document name, e.g. School Employment Letter'}
                                className="input-field"
                              />
                              <button
                                type="button"
                                onClick={() => removeFile(supportFile.id)}
                                className="inline-flex items-center gap-2 text-sm text-red-600 hover:text-red-700 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                                {isZh ? '移除' : 'Remove'}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, 'support')}
                    className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center transition-colors cursor-pointer hover:border-primary hover:bg-primary-50 bg-white"
                  >
                    <input
                      type="file"
                      accept={ALLOWED_EXTENSIONS.join(',')}
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          handleFileSelect(e.target.files[0], 'support');
                          e.currentTarget.value = '';
                        }
                      }}
                      className="hidden"
                      id="support-upload"
                    />
                    <label htmlFor="support-upload" className="cursor-pointer">
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="font-medium text-gray-900">
                        {isZh ? '添加辅助文件' : 'Add support document'}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {isZh ? 'PNG、JPG 或 PDF（最大 5 MB）' : 'PNG, JPG, or PDF (max. 5 MB)'}
                      </p>
                    </label>
                  </div>
                  {errors.support && (
                    <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.support}
                    </p>
                  )}
                </div>

                {/* Upload Summary */}
                {(idFile || certificateFile || supportFiles.length > 0) && (
                  <div className="bg-secondary-50 rounded-lg p-4 border border-secondary-200">
                    <p className="text-sm font-semibold text-gray-900 mb-3">
                      {isZh ? `待上传文件：${uploadedFiles.length}` : `Documents to upload: ${uploadedFiles.length}`}
                    </p>
                    <ul className="space-y-2">
                      {uploadedFiles.map((f) => (
                        <li
                          key={f.id}
                          className="text-sm text-gray-700 flex items-center gap-2"
                        >
                          <div className="w-2 h-2 bg-secondary rounded-full" />
                          {f.type === 'id'
                            ? isZh ? '身份证明文件' : 'Identification Document'
                            : f.type === 'certificate'
                              ? primaryCertificateLabel
                              : f.name || (isZh ? '辅助文件' : 'Support Document')}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {isZh ? '取消' : 'Cancel'}
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-3 bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
                  >
                    {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {uploadProgress || (isZh ? '上传中...' : 'Uploading...')}
                      </>
                    ) : uploadedFiles.length > 0 ? (
                      <>
                        <Upload className="w-4 h-4" />
                        {isZh ? '上传文件' : 'Upload Documents'}
                      </>
                    ) : (
                      isZh ? '暂时跳过' : 'Skip for Now'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Help Text Section */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-2xl">
          <div className="mb-4 rounded-lg border border-primary-200 bg-primary-50 p-6">
            <div className="flex items-start gap-3">
              <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
              <div>
                <h3 className="text-lg font-semibold text-primary-900">
                  {isZh ? '你的隐私受到保护' : 'Your privacy is protected'}
                </h3>
                <p className="mt-2 text-sm leading-6 text-primary-800">
                  {isZh ? '你上传的文件仅用于审核与内部查阅，不会公开展示在导师资料页面。' : 'The documents you upload are used only for verification and internal review. They will not be shown publicly on your tutor profile.'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-6">
            <h3 className="font-semibold text-secondary-900 mb-4 text-lg">
              {isZh ? '上传文件需要帮助吗？' : 'Need help uploading documents?'}
            </h3>
            <ul className="space-y-3 text-sm text-secondary-800">
              <li className="flex gap-3">
                <span className="font-semibold flex-shrink-0">•</span>
                <span>{isZh ? '请确保文件清晰、可阅读' : 'Make sure your documents are clear and readable'}</span>
              </li>
              <li className="flex gap-3">
                <span className="font-semibold flex-shrink-0">•</span>
                <span>{isZh ? '支持格式：PNG、JPG 或 PDF' : 'Supported formats: PNG, JPG, or PDF'}</span>
              </li>
              <li className="flex gap-3">
                <span className="font-semibold flex-shrink-0">•</span>
                <span>{isZh ? '单个文件大小上限为 5 MB' : 'Maximum file size is 5 MB'}</span>
              </li>
              <li className="flex gap-3">
                <span className="font-semibold flex-shrink-0">•</span>
                <span>{isZh ? `${isTeacherRelated ? '教学或教育证书' : '公开考试证书'}或辅助文件可提交资料进入审核；如要获得 Fully Verified 状态，仍需上传身份证明。` : `${isTeacherRelated ? 'Teaching or education certificates' : 'Public Exam Certificates'} or support documents submit your profile for review; ID is required for Fully Verified status`}</span>
              </li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UploadDocumentationPage;
